import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";

const ForgotPasswordView = ({ cLogo, cBgImg }) => {
  const navigate = useNavigate();
  const { handleToastOpen } = useToast();
  
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    } else {
      navigate("/visitor-mgmt/login");
    }
  };

  const handleSendOtp = async () => {
    if (!email) {
      handleToastOpen("error", "Please enter your email address");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      handleToastOpen("error", "Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/forgot-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setStep(2);
        handleToastOpen("success", "OTP sent to your email");
      } else {
        handleToastOpen("error", result.message || "Failed to send OTP");
      }
    } catch (error) {
      handleToastOpen("error", "An error occurred. Please try again later");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) {
      handleToastOpen("error", "Please enter the OTP sent to your email");
      return;
    }

    // OTP validation - should be numeric and 6 digits
    if (!/^\d{6}$/.test(otp)) {
      handleToastOpen("error", "OTP should be 6 digits");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/verify-reset-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email, otp }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setStep(3);
        handleToastOpen("success", "OTP verified successfully");
      } else {
        handleToastOpen("error", result.message || "Invalid OTP");
      }
    } catch (error) {
      handleToastOpen("error", "An error occurred. Please try again later");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      handleToastOpen("error", "Please enter and confirm your new password");
      return;
    }

    if (newPassword !== confirmPassword) {
      handleToastOpen("error", "Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      handleToastOpen("error", "Password must be at least 6 characters long");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/reset-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email,
            otp,
            password: newPassword,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        handleToastOpen("success", "Password reset successfully");
        setTimeout(() => {
          navigate("/visitor-mgmt/login");
        }, 1500);
      } else {
        handleToastOpen("error", result.message || "Failed to reset password");
      }
    } catch (error) {
      handleToastOpen("error", "An error occurred. Please try again later");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BE_URL2}/visitor-mgmt/forgot-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setOtp("");
        handleToastOpen("success", "OTP resent to your email");
      } else {
        handleToastOpen("error", result.message || "Failed to resend OTP");
      }
    } catch (error) {
      handleToastOpen("error", "An error occurred. Please try again later");
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <Typography variant="body1" mb={3} color="text.secondary">
              Enter your email address and we'll send you an OTP to reset your password.
            </Typography>
            <TextField
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              type="email"
              autoFocus
              placeholder="Enter your email"
              InputProps={{
                sx: { borderRadius: "8px" }
              }}
              sx={{ mb: 3 }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleSendOtp}
              disabled={isLoading}
              sx={{
                borderRadius: "8px",
                py: 1.5,
                textTransform: "none",
                fontSize: "16px"
              }}
            >
              {isLoading ? "Sending..." : "Send OTP"}
            </Button>
          </>
        );

      case 2:
        return (
          <>
            <Typography variant="body1" mb={3} color="text.secondary">
              Enter the OTP sent to {email}
            </Typography>
            <TextField
              label="OTP"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
              required
              fullWidth
              placeholder="Enter 6-digit OTP"
              InputProps={{
                sx: { borderRadius: "8px" }
              }}
              sx={{ mb: 1 }}
            />
            <Box sx={{ textAlign: "right", mb: 3 }}>
              <Button
                variant="text"
                color="primary"
                onClick={handleResendOtp}
                disabled={isLoading}
                sx={{ textTransform: "none" }}
              >
                Resend OTP
              </Button>
            </Box>
            <Button
              variant="contained"
              fullWidth
              onClick={handleVerifyOtp}
              disabled={isLoading}
              sx={{
                borderRadius: "8px",
                py: 1.5,
                textTransform: "none",
                fontSize: "16px"
              }}
            >
              {isLoading ? "Verifying..." : "Verify OTP"}
            </Button>
          </>
        );

      case 3:
        return (
          <>
            <Typography variant="body1" mb={3} color="text.secondary">
              Create a new password for your account
            </Typography>
            <TextField
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              fullWidth
              type={showPassword ? "text" : "password"}
              placeholder="Enter new password"
              InputProps={{
                sx: { borderRadius: "8px" },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2.5 }}
            />
            <TextField
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              fullWidth
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your password"
              InputProps={{
                sx: { borderRadius: "8px" },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{ mb: 3 }}
            />
            <Button
              variant="contained"
              fullWidth
              onClick={handleResetPassword}
              disabled={isLoading}
              sx={{
                borderRadius: "8px",
                py: 1.5,
                textTransform: "none",
                fontSize: "16px"
              }}
            >
              {isLoading ? "Resetting..." : "Reset Password"}
            </Button>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        overflow: "hidden",
        width: "100%",
        minHeight: "100vh",
        backgroundImage: `url(${cBgImg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: 3,
      }}
    >
      <Grid
        container
        justifyContent="center"
        alignItems="center"
      >
        <Card
          sx={{
            width: { xs: "90%", sm: "450px" },
            maxWidth: "450px",
            borderRadius: "16px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
            overflow: "hidden",
          }}
        >
          {cLogo && (
            <Box
              sx={{
                bgcolor: "#f7f7f7",
                py: 3,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <img 
                src={cLogo} 
                alt="Company Logo" 
                style={{ 
                  height: "60px", 
                  objectFit: "contain" 
                }} 
              />
            </Box>
          )}
          
          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <IconButton 
                onClick={handleBack}
                sx={{ mr: 1, color: "text.secondary" }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" component="h1" fontWeight="600">
                {step === 1 ? "Forgot Password" : 
                 step === 2 ? "Verify OTP" : 
                 "Reset Password"}
              </Typography>
            </Box>
            
            {renderStepContent()}
          </Box>
        </Card>
      </Grid>
    </Box>
  );
};

export default ForgotPasswordView;